import { Input } from "@components/Input";
import { useTranslatedKey } from "@helpers/useTranslatedKey";
import { useTranslation } from "@helpers/useTranslation";
import { FC, useContext, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useHttpsCallable } from "react-firebase-hooks/functions";
import { FirebaseContext } from "src/helpers/firebase";
import { useEventId } from "src/helpers/useEvent";
import { useSimpleCollection } from "src/helpers/useSimpleCollection";
import { useToast } from "src/helpers/useToast";

/**
 * Admin UI to pre-create "oracle" booth associates and manage which oracle challenges each may
 * arbitrate. Rendered only when the event has mainConfig.oracleMode on (gated by the parent page).
 * All writes go through callables (createOracleAccountV2 / setOracleAssignmentsV2).
 */
export const OracleAssociates: FC = () => {
  const { t } = useTranslation("admin");
  const eventId = useEventId();
  const { functions } = useContext(FirebaseContext);
  const toast = useToast();
  const { getTranslatedValue } = useTranslatedKey();

  const [oracles] = useSimpleCollection<OracleDoc>(`events/${eventId}/oracles`);
  const [challenges] = useSimpleCollection<Challenge>(`events/${eventId}/challenges`);
  const oracleChallenges = useMemo(() => challenges.filter((c) => c.type === "oracle"), [challenges]);

  const [createOracleAccount] = useHttpsCallable<API.CreateOracleAccountRequest, API.CreateOracleAccountResponse>(
    functions,
    "createOracleAccountV2",
  );
  const [setOracleAssignments] = useHttpsCallable<API.SetOracleAssignmentsRequest, API.SetOracleAssignmentsResponse>(
    functions,
    "setOracleAssignmentsV2",
  );

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedChallengeIds, setSelectedChallengeIds] = useState<string[]>([]);
  const [editingUid, setEditingUid] = useState<string | undefined>();
  const [editAssignments, setEditAssignments] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({ defaultValues: { email: "", password: "", displayName: "" } });

  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const onCreate = async (data: { email: string; password: string; displayName: string }) => {
    try {
      setCreating(true);
      await createOracleAccount({
        eventId,
        email: data.email.trim(),
        password: data.password,
        displayName: data.displayName.trim(),
        assignedChallengeIds: selectedChallengeIds,
      });
      toast(t("associateCreated"), "👍");
      setShowCreateModal(false);
      reset();
      setSelectedChallengeIds([]);
    } catch (error) {
      console.error(error);
      toast(t("problemOccured"), "🤷‍♂️", "error");
    } finally {
      setCreating(false);
    }
  };

  const saveAssignments = async (uid: string) => {
    try {
      await setOracleAssignments({ eventId, uid, assignedChallengeIds: editAssignments });
      toast(t("updated"), "👍");
      setEditingUid(undefined);
    } catch (error) {
      console.error(error);
      toast(t("problemOccured"), "🤷‍♂️", "error");
    }
  };

  const challengeTitle = (id: string) => {
    const challenge = oracleChallenges.find((c) => c.id === id);
    return challenge ? getTranslatedValue(challenge, "title") || id : id;
  };

  return (
    <div className="mt-16 flex w-full max-w-6xl flex-col">
      <h2 className="mb-2 text-center text-2xl font-semibold">{t("manageAssociates")}</h2>
      <div className="mb-2 flex justify-end pt-4 sm:mx-8 sm:px-4">
        <button onClick={() => setShowCreateModal(true)} className="btn-primary btn-sm btn">
          {t("createAssociate")}
        </button>
      </div>

      {oracles.length === 0 && <div className="text-center text-lg font-light">{t("noAssociatesYet")}</div>}

      <div className="flex flex-col gap-3">
        {oracles.map((oracle) => {
          const isEditing = editingUid === oracle.uid;
          return (
            <div key={oracle.uid} className="rounded-lg border border-base-300 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">{oracle.displayName}</div>
                  <div className="text-sm text-gray-500">{oracle.email}</div>
                </div>
                <button
                  className="btn-outline btn-xs btn"
                  onClick={() => {
                    setEditingUid(isEditing ? undefined : oracle.uid);
                    setEditAssignments(oracle.assignedChallengeIds ?? []);
                  }}
                >
                  {isEditing ? t("cancel") : t("editAssignments")}
                </button>
              </div>

              {!isEditing && (
                <div className="mt-2 text-sm">
                  {(oracle.assignedChallengeIds ?? []).length === 0
                    ? "—"
                    : (oracle.assignedChallengeIds ?? []).map(challengeTitle).join(", ")}
                </div>
              )}

              {isEditing && (
                <div className="mt-3">
                  <div className="label-text mb-1">{t("assignedChallenges")}</div>
                  <div className="flex flex-col gap-1">
                    {oracleChallenges.map((c) => (
                      <label key={c.id} className="flex cursor-pointer items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          className="checkbox-primary checkbox checkbox-sm"
                          checked={editAssignments.includes(c.id)}
                          onChange={() => setEditAssignments((prev) => toggle(prev, c.id))}
                        />
                        {getTranslatedValue(c, "title") || c.id}
                      </label>
                    ))}
                  </div>
                  <button className="btn-primary btn-sm btn mt-3" onClick={() => saveAssignments(oracle.uid)}>
                    {t("saveAssignments")}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showCreateModal && (
        <div className="modal modal-open">
          <div className="modal-box relative">
            <button
              className="btn-sm btn-circle btn absolute right-2 top-2"
              onClick={() => {
                setShowCreateModal(false);
                reset();
                setSelectedChallengeIds([]);
              }}
            >
              ✕
            </button>
            <h3 className="text-lg font-bold">{t("createAssociate")}</h3>
            <form onSubmit={handleSubmit(onCreate)} className="mt-4">
              <Input
                label={t("associateName")}
                name="displayName"
                required
                requiredMessage={t("fieldRequired")}
                errors={errors}
                register={register}
              />
              <span className="mt-2" />
              <Input
                label={t("associateEmail")}
                name="email"
                type="email"
                required
                requiredMessage={t("fieldRequired")}
                errors={errors}
                register={register}
              />
              <span className="mt-2" />
              <Input
                label={t("associatePassword")}
                name="password"
                type="password"
                required
                requiredMessage={t("fieldRequired")}
                errors={errors}
                register={register}
              />
              <div className="mt-4">
                <div className="label-text mb-1">{t("assignedChallenges")}</div>
                {oracleChallenges.length === 0 && <div className="text-sm text-gray-500">—</div>}
                <div className="flex flex-col gap-1">
                  {oracleChallenges.map((c) => (
                    <label key={c.id} className="flex cursor-pointer items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="checkbox-primary checkbox checkbox-sm"
                        checked={selectedChallengeIds.includes(c.id)}
                        onChange={() => setSelectedChallengeIds((prev) => toggle(prev, c.id))}
                      />
                      {getTranslatedValue(c, "title") || c.id}
                    </label>
                  ))}
                </div>
              </div>
              <div className="my-4 flex justify-center">
                <button type="submit" disabled={creating} className={`btn-primary btn ${creating ? "loading" : ""}`}>
                  {t("createAssociate")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
